<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->

<mat-card appearance="outlined" class="mat-elevation-z4">
  <mat-card-header>
    <mat-card-title>{{ variable.name }}</mat-card-title>
  </mat-card-header>
  <mat-card-actions>
    <button
      color="primary"
      mat-icon-button
      (click)="edit()"
      aria-label="Edit"
      matTooltip="Edit"
      [disabled]="isEditing"
      >
      <mat-icon class="mdi-24px" fontIcon="mdi-square-edit-outline"> </mat-icon>
    </button>
    <button
      mat-icon-button
      ngxClipboard
      color="primary"
      [cbContent]="variable.terraform"
      (click)="$event.stopPropagation()"
      (cbOnSuccess)="onClipboardSuccess()"
      aria-label="Copy Terraform"
      matTooltip="Copy Terraform"
      >
      <mat-icon class="mdi-24px" fontIcon="mdi-content-copy"></mat-icon>
    </button>
    <button
      color="primary"
      mat-icon-button
      (click)="delete()"
      [disabled]="!canEdit"
      aria-label="Delete"
      matTooltip="Delete"
      >
      <mat-icon class="mdi-24px" fontIcon="mdi-trash-can-outline"> </mat-icon>
    </button>
  </mat-card-actions>
  <mat-card-content>
    @if (isEditing) {
      <form [formGroup]="form" (ngSubmit)="save()">
        <div class="d-flex flex-column">
          <mat-form-field floatLabel="always">
            <mat-label [ngClass]="pendingChanges['name'] ? 'changed-hint' : ''"
              >Name</mat-label
              >
              <input
                matInput
                formControlName="name"
                matTooltip="Name of the variable"
                required
                />
              @if (pendingChanges['name']) {
                <mat-hint
                  align="end"
                  class="changed-hint"
                  [matTooltip]="'Click to accept new value:\n' + variable.name"
                  [matTooltipShowDelay]="0"
                  (click)="acceptChange('name')"
                  >
                  Changed
                </mat-hint>
              }
            </mat-form-field>
            <mat-form-field floatLabel="always">
              <mat-label [ngClass]="pendingChanges['type'] ? 'changed-hint' : ''"
                >Type</mat-label
                >
                <mat-select
                  formControlName="type"
                  required
                  (selectionChange)="onTypeChanged($event.value)"
                  >
                  @for (option of typeOptions | keyvalue; track option) {
                    <mat-option
                      [value]="option.value"
                      >
                      {{ option.key }}
                    </mat-option>
                  }
                </mat-select>
                @if (pendingChanges['type']) {
                  <mat-hint
                    align="end"
                    class="changed-hint"
                    [matTooltip]="'Click to accept new value:\n' + variable.type"
                    [matTooltipShowDelay]="0"
                    (click)="acceptChange('type')"
                    >
                    Changed
                  </mat-hint>
                }
              </mat-form-field>
              <mat-form-field
                floatLabel="always"
                matTooltip="Default value for the variable (optional)"
                >
                <mat-label
                  [ngClass]="pendingChanges['defaultValue'] ? 'changed-hint' : ''"
                  >
                  Default
                </mat-label>
                @if (!isMultiline) {
                  <input matInput formControlName="defaultValue" />
                }
                @if (isMultiline) {
                  <textarea
                    matInput
                    formControlName="defaultValue"
                    cdkTextareaAutosize
                    #autosize="cdkTextareaAutosize"
                  ></textarea>
                }
                @if (pendingChanges['defaultValue']) {
                  <mat-hint
                    align="end"
                    class="changed-hint"
            [matTooltip]="
              'Click to accept new value:\n' + variable.defaultValue
            "
                    [matTooltipShowDelay]="0"
                    (click)="acceptChange('defaultValue')"
                    >
                    Changed
                  </mat-hint>
                }
              </mat-form-field>
              <div class="d-flex flex-row justify-content-around align-items-center">
                @if ({ value: isSaving$ | async }; as isSaving) {
                  <button
                    type="submit"
                    color="primary"
                    mat-raised-button
            [disabled]="
              !canEdit || !form.valid || !form.dirty || isSaving.value
            "
                    >
                    @if (!isSaving.value) {
                      <span>Save</span>
                    } @else {
                      <span>Saving...</span>
                    }
                  </button>
                }
                <button
                  type="reset"
                  color="primary"
                  mat-raised-button
                  (click)="cancel()"
                  >
                  @if (!form.dirty) {
                    Close
                  }
                  @if (form.dirty) {
                    Cancel
                  }
                </button>
              </div>
            </div>
          </form>
        }
      </mat-card-content>
    </mat-card>
